import React, {useRef, useEffect, useState} from 'react'
import moment from 'moment'
import _ from 'lodash'

import OrderActions from '../../actions/OrderActions.js'
import useVehicleStore from '../../stores/VehicleStore.js'

import {ColorPicker, FlexRow, FlexColumn, Card, Select, Icon, S1, Badge, ToolTip, Row} from '../UI/index.js'
import {Popover, ListItem, IconButton, Colors, P, Popup, Spinner} from '../UI/index.js'
import time from '../../../server/utils/time.js'
import {formatDuration} from '../../../utils/utils.js'
import {User} from '../../../server/functions/users/users.types.js'
import {FrontEndRoute} from '../../../server/functions/route/route.types.js'
import {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import dbCodes from '../../../server/dbCodes.js'

interface RouteListItemProps {
    users: User[]
    route: FrontEndRoute
    selectedRouteId: string
    focusedRouteId: string
    onEditRoute: (route: FrontEndRoute) => void
    reseller: Reseller
    routes: Record<string, FrontEndRoute>
    view: string
}

export default React.memo(function RouteListItem({users, route, selectedRouteId, focusedRouteId, onEditRoute, reseller, routes, view}: RouteListItemProps) {
    const popupRef = useRef<Popup>(null)
    const titleRef = useRef<S1>(null)
    const [titleIsEllipsis, setTitleIsEllipsis] = useState(false)
    const selected = selectedRouteId === route._id
    const vehicles = useVehicleStore((state) => state.vehicles)

    route.afterTimeSlot = route.addresses?.some((address) => {
        let afterTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            afterTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        } else if (address.duration) {
            afterTimeSlot = moment().add(address.duration || 0, 'seconds').isAfter(moment(address.endTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            afterTimeSlot = moment(address.timeOfArrival, 'HH:mm').isAfter(moment(address.endTime, 'HH:mm'))
        }

        return afterTimeSlot
    })

    route.beforeTimeSlot = !route.afterTimeSlot && route.addresses?.some((address) => {
        let beforeTimeSlot = false

        if (address.estimatedTimeOfArrival) {
            beforeTimeSlot = moment(address.estimatedTimeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
        } else if (address.duration) {
            beforeTimeSlot = moment().add(address.duration || 0, 'seconds').isBefore(moment(address.startTime, 'HH:mm'))
        }

        if (address.timeOfArrival) {
            beforeTimeSlot = moment(address.timeOfArrival, 'HH:mm').isBefore(moment(address.startTime, 'HH:mm'))
        }

        return beforeTimeSlot
    })

    route.inTimeSlots = !route.afterTimeSlot && !route.beforeTimeSlot

    let completedDuration = route.duration || 0
    let completedDelay = route.delay || 0

    if (route.completed && route.endTime && route.startTime) {
        completedDuration = moment(route.endTime, 'HH:mm').diff(moment(route.startTime, 'HH:mm'), 'seconds')
        completedDelay = completedDuration - route.plannedDuration
    }

    const uniqueColli = route.colli?.filter((c, index, self) => c.barcode ? index === self.findIndex((collo) => collo.barcode === c.barcode) : true)

    const amountOfColli = uniqueColli?.length || 0

    const vehicleType = reseller.settings.vehicleTypes.find((v) => v._id === route.vehicleTypeId)

    const vehicle = vehicles.find((vehicle) => vehicle._id === route.vehicleId)

    let tooMuchColli: Reseller['settings']['vehicleTypes'][0]['payloads'][0]['payloadColliType'] | boolean = false

    vehicleType?.payloads?.forEach((payload) => {
        if (tooMuchColli) {
            return
        }

        if (payload.payloadType === 'colli') {
            if (payload.payloadColliType) {
                const amountOfColliType = uniqueColli?.filter((collo) => collo.type === payload.payloadColliType).length
                if (payload.payload > 0 && amountOfColliType > payload.payload) {
                    tooMuchColli = payload.payloadColliType
                }
            } else if (payload.payload > 0 && amountOfColli > payload.payload) {
                tooMuchColli = true
            }
        }
    })

    let totalWeight = 0

    uniqueColli?.forEach((collo) => {
        totalWeight += collo.weight
    })

    const tooHeavy = _.some(vehicleType?.payloads, (payload) => {
        if (payload.payloadType === 'weight') {
            return payload.payload > 0 && totalWeight > payload.payload
        }
    })

    const tooFar = vehicleType?.range && vehicleType.range > 0 && route.distance > vehicleType.range

    const vehicleRoutes = Object.values(routes).filter((r) => vehicle && r.vehicleId === vehicle?._id && r._id !== route._id && (!r.completed && !route.completed))

    const thisRouteStart = route.plannedStartTime || route.startTime
    const thisRouteEnd = route.estimatedEndTime || route.endTime

    const routeOverlaps = vehicleRoutes.some((r) => {
        const startTime = r.plannedStartTime || r.startTime
        const endTime = r.estimatedEndTime || r.endTime

        return moment(thisRouteStart, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '[)') || moment(thisRouteEnd, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '(]')
    })

    const messengerRoutes = Object.values(routes).filter((r) => route.messengerId && r.messengerId === route.messengerId && r._id !== route._id && (!r.completed && !route.completed))

    const messengerOverlaps = messengerRoutes.some((r) => {
        const startTime = r.plannedStartTime || r.startTime
        const endTime = r.estimatedEndTime || r.endTime

        return moment(thisRouteStart, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '[)') || moment(thisRouteEnd, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '(]')
    })

    useEffect(() => {
        const el = titleRef?.current?.ref
        if (el) {
            if (el.offsetWidth < el.scrollWidth) {
                setTitleIsEllipsis(true)
            }
        }
    }, [titleRef.current])

    return (
        <Card key={route._id} data-route-id={route._id} shadow={true} selected={selected} style={{padding: 0, marginBottom: 6, scrollMarginTop: 20}}>
            <FlexRow style={{height: '100%', alignItems: 'stretch'}}>
                <FlexColumn style={{padding: 6}}>
                    <FlexRow style={{alignItems: 'center'}}>
                        {route.completed ?
                                <Icon
                                    style={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, fontSize: 20, color: route.color}}
                                    icon='mdi mdi-circle'
                                /> :
                                <ColorPicker
                                    value={route.color}
                                    colors={Colors.routeColors}
                                    onChange={(event) => OrderActions.updateRoute(route._id, {color: event.target.value})}
                                />
                        }

                        <Row style={{flex: 2, alignItems: 'center', maxWidth: 140, overflowX: 'hidden'}}>
                            {view !== 'expanded' && !selected && (tooMuchColli || tooHeavy || tooFar || routeOverlaps || messengerOverlaps || ((route.plannedStartTime || route.startTime) && (!route.inTimeSlots && route.afterTimeSlot))) &&
                                <Icon icon='mdi mdi-alert' style={{color: Colors.errorBright}} />
                            }

                            {titleIsEllipsis ?
                                    (
                                        <ToolTip placement='right' style={{overflowX: 'hidden'}} tooltipStyle={{width: 'fit-content', whiteSpace: 'nowrap', padding: '0 8px', marginLeft: -70, maxWidth: 350}} text={route.name}>
                                            <S1 ref={titleRef} ellipsis style={{flex: 2, cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>{route.name}</S1>
                                        </ToolTip>
                                    ) :
                                    (
                                        <S1 ref={titleRef} ellipsis style={{flex: 2, cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>{route.name}</S1>
                                    )}
                        </Row>

                        <FlexColumn style={{alignItems: 'center', cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>
                            <FlexRow style={{alignItems: 'center', justifyContent: 'flex-end'}}>
                                <FlexColumn>
                                    {!route.ready && !route.started && !route.completed &&
                                        <Badge variant='grey'>Concept</Badge>
                                    }

                                    {route.ready && !route.started && !route.completed &&
                                        <Badge color={Colors.infoPale}>Gereed</Badge>
                                    }

                                    {route.started && !route.completed &&
                                        <Badge textColor={Colors.textDark} variant='info'>Onderweg</Badge>
                                    }

                                    {route.completed &&
                                        <Badge variant='success'>Afgerond</Badge>
                                    }
                                </FlexColumn>

                                <FlexColumn style={{marginLeft: 12, width: 50}}>
                                    <P>{route.plannedStartTime || route.startTime}</P>
                                </FlexColumn>
                            </FlexRow>
                        </FlexColumn>

                        {!route.completed &&
                            <Popover
                                placement='left'
                                content={
                                    <div style={{width: 'fit-content'}}>
                                        <ListItem
                                            size='sm'
                                            icon={focusedRouteId === route?._id ? 'mdi mdi-image-filter-center-focus-weak' : 'mdi mdi-image-filter-center-focus-strong'}
                                            onClick={() => OrderActions.setRouteFocus(route?._id)}
                                        >

                                            {route._id === focusedRouteId ? 'Reset focus' : 'Focus route'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon={route.hidden ?
                                                'mdi mdi-map-marker' :
                                                'mdi mdi-map-marker-off'
                                            }
                                            onClick={() => OrderActions.setRouteHidden(route._id)}
                                        >

                                            {route.hidden ? 'Toon route' : 'Verberg route'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-tune'
                                            onClick={() => onEditRoute(route)}
                                        >
                                            Route instellingen
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-map-marker-path'
                                            disabled={route.ready}
                                            onClick={() => OrderActions.planRoute(route._id, true)}
                                        >
                                            Optimaliseer route
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-swap-vertical'
                                            disabled={route.ready || route.started || !route.reversible}
                                            onClick={() => OrderActions.planRoute(route._id, false, true)}
                                        >
                                            Route omdraaien
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon={route.ready ? 'mdi mdi-pencil' : 'mdi mdi-check'}
                                            onClick={async () => {
                                                if (route.ready) {
                                                    await popupRef.current.open('Route wijzigen', 'Weet je zeker dat je deze route wilt wijzigen? ')
                                                } else {
                                                    const warnings = []
                                                    if (tooMuchColli) {
                                                        warnings.push('Deze route bevat meer colli dan het laadvermogen van het geselecteerde voertuig.')
                                                    }

                                                    if (tooHeavy) {
                                                        warnings.push('Deze route bevat colli die zwaarder zijn dan het laadvermogen van het geselecteerde voertuig.')
                                                    }

                                                    if (tooFar) {
                                                        warnings.push('Deze route is langer dan het bereik van het geselecteerde voertuig.')
                                                    }

                                                    if ((route.plannedStartTime || route.startTime) && !route.inTimeSlots) {
                                                        warnings.push('Deze route haalt niet alle stops binnen het aangegeven tijdvak.')
                                                    }

                                                    if (routeOverlaps) {
                                                        warnings.push('Deze route heeft een dubbel geboekt voertuig.')
                                                    }

                                                    if (messengerOverlaps) {
                                                        warnings.push('Deze route heeft een dubbel geboekte koerier.')
                                                    }

                                                    if (warnings.length) {
                                                        popupRef.current.setWarnings(warnings)
                                                        await popupRef.current.open('Route gereed maken', 'Weet je zeker dat je door wil gaan?')
                                                    }
                                                }

                                                OrderActions.setRouteReady(route._id, !route.ready, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >
                                            {route.ready ? 'Route wijzigen' : 'Route gereed'}
                                        </ListItem>

                                        <ListItem
                                            size='sm'
                                            icon='mdi mdi-calendar'
                                            onClick={async () => {
                                                popupRef.current.setInput({type: 'date', value: time.tomorrow(), label: 'Datum'})
                                                const date = await popupRef.current.open('Route verplaatsen', 'Weet je zeker dit zeker?')

                                                OrderActions.rescheduleRoute(route._id, date, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >

                                            Route verplaatsen
                                        </ListItem>

                                        <ListItem
                                            style={{color: route?.ready || route?.started || route?.completed ? undefined : Colors.errorBright}}
                                            size='sm'
                                            icon='mdi mdi-delete'
                                            disabled={route?.ready || route?.started || route?.completed}
                                            onClick={async () => {
                                                if (route.addresses?.length) {
                                                    await popupRef.current.open('Route verwijderen', 'Deze route bevat stops, weet je zeker dat je deze route wilt verwijderen?')
                                                }

                                                OrderActions.removeRoute(route._id, (err) => {
                                                    if (err) {
                                                        popupRef.current.setError(err)
                                                    } else {
                                                        popupRef.current.close()
                                                    }
                                                })
                                            }}
                                        >
                                            Route verwijderen
                                        </ListItem>
                                    </div>
                                }
                            >
                                <IconButton icon='mdi mdi-dots-vertical' />
                            </Popover>
                        }
                    </FlexRow>

                    {(view === 'expanded' || selected) &&
                        <>
                            <FlexRow>
                                <FlexColumn style={{paddingLeft: 6, paddingRight: 6}}>
                                    <Select
                                        style={{width: '100%'}}
                                        label='Koerier'
                                        allowEmptyValue
                                        options={users.map((user) => {
                                            const thisUserWouldOverlap = Object.values(routes).filter((r) => r.messengerId === user._id && r._id !== route._id && (!r.completed && !route.completed)).some((r) => {
                                                const startTime = r.plannedStartTime || r.startTime
                                                const endTime = r.estimatedEndTime || r.endTime

                                                return moment(thisRouteStart, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '[)') || moment(thisRouteEnd, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '(]')
                                            })

                                            const thisUserOverlaps = messengerOverlaps && route.messengerId === user._id

                                            let icon

                                            if (thisUserWouldOverlap) {
                                                icon = 'cancel'
                                            }

                                            if (thisUserOverlaps) {
                                                icon = 'alert'
                                            }

                                            return {value: user._id, title: user.name, ...(icon && {icon: `mdi mdi-${icon}`}), ...(thisUserOverlaps && {iconColor: Colors.errorBright})}
                                        })}
                                        value={route.messengerId}
                                        readOnly={route.completed}
                                        onChange={(event) => OrderActions.updateRoute(route._id, {messengerId: event.target.value})}
                                        isInvalid={messengerOverlaps}
                                        smartPlacement={false}
                                    />
                                </FlexColumn>

                                <FlexColumn style={{paddingLeft: 6, paddingRight: 6}}>
                                    <Select
                                        style={{width: '100%'}}
                                        label='Voertuig'
                                        options={[
                                            // This sorting is overruled in the select component by the groupSubvalues prop
                                            ...vehicles.sort((a, b) => {
                                                const aInFutureRange = a.notAvailableDateTimeRanges?.find((range) => moment(route.date).isBetween(range.start, range.end, 'day', '[]'))
                                                const bInFutureRange = b.notAvailableDateTimeRanges?.find((range) => moment(route.date).isBetween(range.start, range.end, 'day', '[]'))

                                                const aArchived = a.isArchived ? 1 : 0
                                                const bArchived = b.isArchived ? 1 : 0

                                                if (aArchived !== bArchived) {
                                                    return aArchived - bArchived
                                                } else if (aInFutureRange && bInFutureRange) {
                                                    return (aInFutureRange.end > bInFutureRange.end) ? 1 : -1
                                                } else if (aInFutureRange) {
                                                    return 1
                                                } else if (bInFutureRange) {
                                                    return -1
                                                }

                                                return 0
                                            }).map((vehicle) => {
                                                const activeVehicleType = reseller.settings.vehicleTypes.find((type) => type._id === vehicle.typeId)

                                                const thisVehicleWouldOverlap = Object.values(routes).filter((r) => r.vehicleId === vehicle._id && r._id !== route._id && (!r.completed && !route.completed)).some((r) => {
                                                    const startTime = r.plannedStartTime || r.startTime
                                                    const endTime = r.estimatedEndTime || r.endTime

                                                    return moment(thisRouteStart, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '[)') || moment(thisRouteEnd, 'HH:mm').isBetween(moment(startTime, 'HH:mm'), moment(endTime, 'HH:mm'), 'minute', '(]')
                                                })

                                                const thisVehicleOverlaps = routeOverlaps && route.vehicleId === vehicle._id

                                                const inFutureRange = vehicle.notAvailableDateTimeRanges?.find((range) => moment(route.date).isBetween(range.start, range.end, 'day', '[]'))

                                                let icon = activeVehicleType?.icon
                                                if (thisVehicleWouldOverlap) {
                                                    icon = 'cancel'
                                                }

                                                if (thisVehicleOverlaps) {
                                                    icon = 'alert'
                                                }

                                                return {
                                                    value: activeVehicleType?._id,
                                                    subvalue: vehicle._id,
                                                    title: vehicle.name,
                                                    icon: `mdi mdi-${icon}`,
                                                    ...(thisVehicleOverlaps && {iconColor: Colors.errorBright}),
                                                    hidden: activeVehicleType?.isArchived || vehicle.isArchived,
                                                    disabled: inFutureRange
                                                }
                                            }),
                                            ...reseller.settings.vehicleTypes.sort((a, b) => {
                                                const aArchived = a.isArchived ? 1 : 0
                                                const bArchived = b.isArchived ? 1 : 0

                                                return aArchived - bArchived
                                            }).map((vehicleType) => ({
                                                value: vehicleType._id,
                                                title: `${vehicleType.name}`,
                                                icon: `mdi mdi-${vehicleType.icon}`,
                                                hidden: vehicleType.isArchived
                                            }))
                                        ]}
                                        value={route.vehicleTypeId}
                                        subvalue={route.vehicleId}
                                        readOnly={route.completed}
                                        onChange={(event) => {
                                            const vehicleType = reseller.settings.vehicleTypes.find((vehicleType) => vehicleType._id === event.target.value)

                                            if (!vehicleType) {
                                                return
                                            }

                                            const vehicle = vehicles.find((v) => v._id === event.target.subvalue)

                                            const update = {
                                                vehicleTypeId: vehicleType._id,
                                                vehicleId: vehicle?._id || '',
                                                vehicleType: vehicleType.routeProfile || '',
                                                customSpeed: false,
                                                speedDifference: 0
                                            }

                                            if (vehicleType?.speedDifferenceEnabled) {
                                                update.customSpeed = true
                                                const speedDifference = vehicleType.speedDifferenceMode === 'faster' ? -vehicleType.speedDifference : vehicleType.speedDifference
                                                update.speedDifference = speedDifference
                                            } else {
                                                update.customSpeed = false
                                                update.speedDifference = 0
                                            }

                                            OrderActions.updateRoute(route._id, update)
                                        }}
                                        isInvalid={routeOverlaps}
                                        smartPlacement={false}
                                        groupSubvalues
                                    />
                                </FlexColumn>
                            </FlexRow>

                            {route.loading &&
                                <FlexRow style={{justifyContent: 'center', alignItems: 'center', height: 42}}>
                                    <Spinner />
                                </FlexRow>
                            }

                            {!route.loading && !route.completed &&
                                <FlexRow style={{justifyContent: 'space-between', gap: 12, cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>

                                    <ToolTip text='Aantal adressen' placement='top' style={{flex: 1}} tooltipStyle={{marginLeft: -36}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-home-outline' />

                                            <P>{route.addresses?.length || 0}</P>
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text={(tooMuchColli || tooHeavy) ? `Laadvermogen overschreden (${tooHeavy ? 'Gewicht' : typeof tooMuchColli === 'string' ? dbCodes.colloTypes[tooMuchColli] : 'Colli'})` : 'Aantal colli'} placement='top' style={{flex: 1}} tooltipStyle={{...(tooMuchColli || tooHeavy) && {marginLeft: -108}}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0, ...((tooMuchColli || tooHeavy) && {color: Colors.errorBright})}} icon='mdi mdi-package-variant-closed' />

                                            <P style={{...((tooMuchColli || tooHeavy) && {color: Colors.errorBright})}}>{amountOfColli}</P>
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text={tooFar ? 'Bereik overschreden' : 'Resterende afstand'} placement='top' style={{flex: 1}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0, ...(tooFar && {color: Colors.errorBright})}} icon='mdi mdi-road-variant' />

                                            {typeof route.distance === 'number' &&
                                                <P style={{...(tooFar && {color: Colors.errorBright})}}>
                                                    {route.distance < 10000 ?
                                                        `${(route.distance / 1000).toFixed(1)} km` :
                                                        `${(route.distance / 1000).toFixed(0)} km`
                                                    }
                                                </P>
                                            }
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text='Resterende duur' placement='top' style={{flex: 1}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon
                                                style={{marginRight: 0}}
                                                icon={!route.startTime && !route.inTimeSlots ? 'mdi mdi-timer-sand-complete' : 'mdi mdi-timer-sand'}
                                            />

                                            {typeof route.duration === 'number' &&
                                                <P>{formatDuration(route.duration)}</P>
                                            }
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text='Verwachte eindtijd' placement='top' style={{flex: 1}} tooltipStyle={{marginLeft: -100}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon
                                                style={{marginRight: 0, color: (route.plannedStartTime || route.startTime) && !route.inTimeSlots ? (route.afterTimeSlot ? Colors.errorBright : Colors.warningBright) : undefined}}
                                                icon={(route.plannedStartTime || route.startTime) && !route.inTimeSlots ? 'mdi mdi-clock-alert-outline' : 'mdi mdi-clock-check-outline'}
                                            />

                                            {route.estimatedEndTime &&
                                                <P>{route.estimatedEndTime}</P>
                                            }
                                        </FlexColumn>
                                    </ToolTip>
                                </FlexRow>
                            }

                            {route.completed &&
                                <FlexRow style={{justifyContent: 'space-between', gap: 12, cursor: 'pointer'}} onClick={() => OrderActions.setSelectedRoute(route._id)}>
                                    <ToolTip text='Afgerond' placement='top' style={{flex: 1}} tooltipStyle={{marginLeft: -30}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-check-bold' />

                                            <P>{route.success || 0}</P>
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text='Niet afgerond' style={{flex: 1}} placement='top'>
                                        <FlexColumn style={{flex: 1, alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-close-thick' />

                                            <P>{route.notCompleted || 0}</P>
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text='Te laat' placement='top' style={{flex: 1}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon style={{marginRight: 0}} icon='mdi mdi-clock-remove-outline' />

                                            <P>{route.tooLate || 0}</P>
                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text='Duur' placement='top' style={{flex: 1}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon
                                                style={{marginRight: 0}}
                                                icon='mdi mdi-timer-sand-complete'
                                            />

                                            {typeof completedDuration === 'number' &&
                                                <P>{formatDuration(completedDuration)}</P>
                                            }

                                        </FlexColumn>
                                    </ToolTip>

                                    <ToolTip text='Afwijking berekende duur' placement='top' style={{flex: 1}} tooltipStyle={{marginLeft: -150}}>
                                        <FlexColumn style={{alignItems: 'center'}}>
                                            <Icon icon='mdi mdi-flag-checkered' />

                                            {typeof completedDelay === 'number' &&
                                                <P>{formatDuration(completedDelay, true)}</P>
                                            }
                                        </FlexColumn>
                                    </ToolTip>
                                </FlexRow>
                            }
                        </>
                    }
                </FlexColumn>

                <FlexRow
                    style={{
                        width: 24,
                        backgroundColor: Colors.backgroundSelected,
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => OrderActions.setSelectedRoute(selected ? '' : route._id)}
                >
                    <Icon icon={selected ? 'mdi mdi-chevron-left' : 'mdi mdi-chevron-right'} />
                </FlexRow>
            </FlexRow>

            <Popup ref={popupRef} />
        </Card>
    )
})
